import React from 'react';
import { ScrollView, Text, View } from 'react-native';
import i18n from 'i18n-js';

import { bottomBarHeight, fullWidth, scale as initialScale } from '@components/frame';

import {
  Popup,
  ncFileStatuses,
  getLinkingModalConfig,
  isPhone,
  isWeb,
  withERP,
} from '../../../../../../../helpers';

import { font as initialFont } from '@styles/fonts';

import NcFileContent from '@components/common/NCFile';

import InputLine from '@components/machines/components/detailsComponents/activeMachine/components/InputLine';
import NCFileType from '@components/common/NCFileType';

import FeatureFlags from '@components/featureFlags';
import {
  BOTTOM_SHEET_ENABLED,
  SCREEN_BORDER_RADIUS,
  SCREEN_PADDING_HORIZONTAL_FOR_ITEM,
  TEST_IDS,
} from '../../../../../../v2/pages/common/constants/base.constants';
import ImagesContainer, { ImagesContainerMode } from '@v2/pages/common/components/ImagesContainer/ImagesContainer';
import { ItemName } from '@v2/pages/batch/details/modules/batch/components/BatchDetailsBlock/BatchDetailsBlockContent';
import LinkIcon from '@v2/context/ProductionBatch/linkIcon';
import { ShowFullNcFileButton } from '@v2/elements/ShowFullNcFileButton/ShowFullNcFileButton';
import { Portal } from 'react-native-portalize';
import { DOCUMENT_ENTITY_TYPE } from '@components/imageHandling/common';
import ProductivityBlockWithTimelines from '@components/common/ProductivityBlockWithTimelines';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import ReadyPcsQuantity from '@components/common/ReadyPcsV2/components/ReadyPcsQuantity';
import PiecesPerCycleModal from '@components/machines/components/detailsComponents/activeMachine/components/PiecesPerCycleModal';
import { addProductionInformation } from '@reducers/manufacture';
import { useManageNCFile } from './hooks/useManageNCFile';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

const NCFileBlock = (props) => {
  const dispatch = useAppDispatch();
  
  const {
    C,
    selectedItem,
    font = initialFont,
    scale = initialScale,
    withImages,
    maxDetailsWidth,
    shouldTruncate = false,
    shouldScroll = true,
    bg = 'Background/Light/Field',
  } = props;

  const {
    isFocused,
    safeIsMachinesScreenCheck,
    safeIsDetailsScreen,
    isNcFilePerspective,
    batch,
    isWithERP,
    ncFileData,
    shouldShowGlobalFullNCFileButton,
    shouldShowFullNCFileButtonInternal,
    containerCoordinates,
    showFullNCFileButtonWidth,
    setShowFullNCFileButtonWidth,
    ncFileName,
    setNcFileName,
    optimisticPiecesPerCycle,
    setOptimisticPiecesPerCycle,
    scrollContainerRef,
    ncFile,
    ncFileId,
    ncFileStatus,
    clampingDetails,
    shouldProvideBatchIdDuringLinking,
    toVersions,
    versionNumber,
    ncFileStatusMessage,
    roundTopConfig,
    onNCFileNameClick,
    onNCFileImageClick,
    shouldHandleProductiveTimes,
    leftForShowFullNCFileButton,
    shadowStyles,
    productiveTimeBlockConfig,
    handleSetShouldShowGlobalFullNCFileButton,
  } = useManageNCFile({ selectedItem, scale, C });

  const ShowFullNcFileButtonPortal = (
    <View
      onLayout={(event) => {
        const width = event.nativeEvent.layout.width;
        setShowFullNCFileButtonWidth(width);
      }}
      style={{
        position: 'absolute',
        bottom: scale(17 + (isWeb ? 0 : bottomBarHeight)),
        opacity: !showFullNCFileButtonWidth ? 0 : 1,
        left: leftForShowFullNCFileButton,
      }}
    >
      <ShowFullNcFileButton C={C} ncFileId={ncFileId} ncFileName={ncFileName} />
    </View>
  );

  return (
    <View ref={scrollContainerRef} style={{ flex: 1 }}>
      <ScrollView
        contentContainerStyle={{
          minHeight: '100%',
          paddingHorizontal: scale(SCREEN_PADDING_HORIZONTAL_FOR_ITEM),
          paddingTop: scale(2),
          paddingBottom: scale(20),
        }}
        scrollEventThrottle={16}
      >
        <View
          style={[
            shadowStyles,
            {
              borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
              borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
            },
            roundTopConfig,
          ]}
        >
          {withImages && (
            <ImagesContainer
              {...{
                maxDetailsWidth,
                panelViewMode: false,
                mode: ImagesContainerMode.ROW,
                bg,
                documentRetrieveKey: DOCUMENT_ENTITY_TYPE.NcFile,
                documentRetrieveId: ncFileId,
                onImagePress: onNCFileImageClick,
              }}
            />
          )}

          {(!!ncFileData || safeIsMachinesScreenCheck) && (
            <View
              style={[
                {
                  backgroundColor: C(bg),
                  borderBottomLeftRadius: scale(SCREEN_BORDER_RADIUS),
                  borderBottomRightRadius: scale(SCREEN_BORDER_RADIUS),
                  overflow: 'hidden',
                },
                !withImages && roundTopConfig,
              ]}
            >
              <TourTarget tourId={WELCOME_TOUR_IDS.NC_FILE_NAME}>
                <InputLine
                  {...{
                    bg,
                    font,
                    scale,
                    data: [{}],
                    index: 0,
                    endLine: true,
                    i: {
                      name: 'NC File',
                      isText: isNcFilePerspective,
                      ...(isNcFilePerspective
                        ? { value: ncFile }
                        : {
                            ValueComponent: <ItemName {...{ C, onPress: onNCFileNameClick, text: ncFile }} />,
                          }),
                    },
                    C,
                  }}
                />
              </TourTarget>

              <TourTarget tourId={WELCOME_TOUR_IDS.NC_FILE_TYPE}>
                <FeatureFlags {...{ name: 'ncfile-update-nc-file-type' }}>
                  <InputLine
                    {...{
                      testID: TEST_IDS.ncFileBlock.ncFileTypeRow,
                      bg,
                      font,
                      scale,
                      data: [{}],
                      index: 0,
                      borderBottomRadius: false,
                      endLine: true,
                      i: {
                        action: () =>
                          Popup({
                            Component: (p) => (
                              <NCFileType
                                {...{
                                  ...p,
                                  selectedItem,
                                  ncFileId,
                                  ncFileName: ncFile,
                                  ...(shouldProvideBatchIdDuringLinking ? { productionBatchId: batch?.productionBatchId } : {}),
                                }}
                              />
                            ),
                            ...getLinkingModalConfig(scale, fullWidth),
                          }),
                        name: ncFileStatus === ncFileStatuses.Unknown ? 'Update NC File Type' : 'NC File Type',
                        ValueComponent: (
                          <View style={{ gap: scale(9), alignItems: 'center', flexDirection: isPhone ? 'row-reverse' : 'row' }}>
                            <LinkIcon isWithERP={withERP(isWithERP)} linkStatus="" ncFileStatus={ncFileStatus} takeIntoAccountOnlyNcFileStatus />

                            {!isPhone && (
                              <Text style={[font.Body, { color: C('Label/Light/Tertiary') }]}>{i18n.t(ncFileStatusMessage[ncFileStatus])}</Text>
                            )}
                          </View>
                        ),
                      },
                      C,
                    }}
                  />
                </FeatureFlags>
              </TourTarget>

              <InputLine
                {...{
                  testID: TEST_IDS.ncFileBlock.piecesPerCycleRow,
                  bg,
                  font,
                  scale,
                  data: [{}],
                  index: 0,
                  borderBottomRadius: !clampingDetails?.memo,
                  endLine: !!clampingDetails?.memo,
                  i: {
                    action: () => {
                      const currentValue = optimisticPiecesPerCycle ?? ncFileData.numberOfPiecesPerCycle ?? 1;

                      Popup({
                        Component: (p) => (
                          <PiecesPerCycleModal
                            hide={p.hide}
                            C={C}
                            font={font}
                            scale={scale}
                            currentValue={currentValue}
                            onSubmit={async (value) => {
                              setOptimisticPiecesPerCycle(value);
                              await dispatch(
                                addProductionInformation({
                                  ncFileId,
                                  clampingDetails: { numberOfPiecesPerCycle: value },
                                })
                              );
                            }}
                          />
                        ),
                        shouldCloseOnPressOutside: true,
                      });
                    },
                    name: 'Pieces Per Cycle',
                    ValueComponent: (
                      <View style={{ flexDirection: 'row', alignItems: 'baseline', flex: 1 }}>
                        <ReadyPcsQuantity C={C} quantityProduced={optimisticPiecesPerCycle ?? ncFileData.numberOfPiecesPerCycle ?? 1} />
                        <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary') }]}> / {i18n.t('Cycle')}</Text>
                      </View>
                    ),
                  },
                  C,
                }}
              />
            </View>
          )}

          {!!clampingDetails?.memo && (
            <View style={{ marginTop: scale(20), width: '100%', borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' }}>
              <InputLine
                {...{
                  bg,
                  font,
                  scale,
                  borderBottomRadius: true,
                  data: [{}],
                  index: 0,
                  i: {
                    name: 'Note',
                    value: clampingDetails?.memo,
                  },
                  C,
                }}
              />
            </View>
          )}
        </View>

        {shouldHandleProductiveTimes && !!productiveTimeBlockConfig.length && (
          <ProductivityBlockWithTimelines
            {...{
              C,
              data: productiveTimeBlockConfig,
              label: i18n.t('CYCLE TIME PER PIECE'),
              labelAlreadyTranslated: true,
              onlyLabel: true,
            }}
          />
        )}

        <TourTarget tourId={WELCOME_TOUR_IDS.NC_FILE_CONTENT}>
          <View
            style={[
              shadowStyles,
              {
                backgroundColor: C(bg),
                flex: 1,
                marginTop: scale(20),
                borderRadius: scale(SCREEN_BORDER_RADIUS),
              },
            ]}
          >
            <View
              style={{
                width: '100%',
                borderRadius: scale(SCREEN_BORDER_RADIUS),
                overflow: 'hidden',
              }}
            >
              {Boolean(versionNumber) && (
                <FeatureFlags {...{ name: 'ncfile-versions' }}>
                  <InputLine
                    {...{
                      testID: TEST_IDS.ncFileBlock.versionRow,
                      bg,
                      font,
                      scale,
                      data: [],
                      index: 1,
                      i: {
                        action: toVersions,
                        name: 'Version',
                        value: versionNumber,
                        isText: true,
                      },
                      C,
                    }}
                  />
                </FeatureFlags>
              )}
            </View>
            <View style={{ flex: 1 }}>
              <NcFileContent
                {...{
                  selectedItem,
                  C,
                  font,
                  shouldTruncate,
                  shouldScroll,
                  setNcFileName,
                  setShouldShowGlobalFullNCFileButton: handleSetShouldShowGlobalFullNCFileButton,
                  hideShowFullNCFileButton: !shouldShowGlobalFullNCFileButton,
                  backgroundColor: bg,
                }}
              />
            </View>
          </View>
        </TourTarget>
      </ScrollView>

      {!!containerCoordinates && shouldShowGlobalFullNCFileButton && shouldShowFullNCFileButtonInternal && isFocused && (
        <>{safeIsDetailsScreen && BOTTOM_SHEET_ENABLED ? ShowFullNcFileButtonPortal : <Portal>{ShowFullNcFileButtonPortal}</Portal>}</>
      )}
    </View>
  );
};

export default NCFileBlock;
